import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, ArrowUpCircle, GripVertical, Layers, Crown } from 'lucide-react';
import { upgradesApi, PLACEHOLDER_IMAGE } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { ImageUpload } from '../upload/ImageUpload';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

// ─── Seltenheits-Konfiguration (gleich wie Gachas) ───────────────────────────
const RARITIES = [
    'Common', 'Uncommon', 'Rare', 'Epic', 'Legendary',
    'Mythical', 'Monarch', 'Limited', 'ULimited'
];

const RARITY_COLORS = {
    Common:    { bg: 'bg-gray-500/10',   border: 'border-gray-500/30',   text: 'text-gray-400',   badge: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    Uncommon:  { bg: 'bg-green-500/10',  border: 'border-green-500/30',  text: 'text-green-400',  badge: 'bg-green-500/20 text-green-400 border-green-500/30' },
    Rare:      { bg: 'bg-blue-500/10',   border: 'border-blue-500/30',   text: 'text-blue-400',   badge: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    Epic:      { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', badge: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
    Legendary: { bg: 'bg-amber-500/10',  border: 'border-amber-500/30',  text: 'text-amber-400',  badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    Mythical:  { bg: 'bg-red-500/10',    border: 'border-red-500/30',    text: 'text-red-400',    badge: 'bg-red-500/20 text-red-400 border-red-500/30' },
    Monarch:   { bg: 'bg-pink-500/10',   border: 'border-pink-500/30',   text: 'text-pink-400',   badge: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
    Limited:   { bg: 'bg-sky-500/10',    border: 'border-sky-500/30',    text: 'text-sky-400',    badge: 'bg-sky-500/20 text-sky-400 border-sky-500/30' },
    ULimited:  { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', badge: 'bg-violet-500/20 text-violet-400 border-violet-500/30' },
};

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────
const makeVariant = (name = '') => ({
    variant_name: name,
    levels: [],
});

const makeLevel = (levelNum) => ({
    level: levelNum,
    fields: [],
});

// ─── Haupt-Komponente ──────────────────────────────────────────────────────────
export const UpgradeManager = ({ island }) => {
    const { user } = useAuth();
    const [upgrades, setUpgrades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingUpgrade, setEditingUpgrade] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [upgradeToDelete, setUpgradeToDelete] = useState(null);
    const [expandedUpgrade, setExpandedUpgrade] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);

    /*
     * formData.variants_by_rarity ist ein Array von:
     * {
     *   rarity: string,
     *   variants: [
     *     {
     *       variant_name: string,
     *       levels: [
     *         { level: number, fields: [{ name: string, value: string }] }
     *       ]
     *     }
     *   ]
     * }
     */
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        image_url: '',
        variants_by_rarity: [],
    });

    useEffect(() => {
        if (island?.id) loadUpgrades();
    }, [island?.id]);

    const loadUpgrades = async () => {
        setLoading(true);
        try {
            const response = await upgradesApi.getAll(island.id);
            const data = response.data;
            setUpgrades(Array.isArray(data) ? data : (data.items || []));
        } catch (error) {
            console.error('Failed to load upgrades:', error);
            toast.error('Fehler beim Laden der Upgrades');
        } finally {
            setLoading(false);
        }
    };

    // ─── Drag & Drop ────────────────────────────────────────────────────────────
    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedItem === null || draggedItem === index) return;
        const arr = [...upgrades];
        const [item] = arr.splice(draggedItem, 1);
        arr.splice(index, 0, item);
        setUpgrades(arr);
        setDraggedItem(index);
    };

    const handleDragEnd = async () => {
        if (draggedItem === null) return;
        const updates = upgrades.map((u, i) => ({ id: u.id, order: i }));
        try {
            await upgradesApi.reorder(updates);
            toast.success('Reihenfolge gespeichert');
        } catch {
            loadUpgrades();
        }
        setDraggedItem(null);
    };

    // ─── Formular-Initialisierung ───────────────────────────────────────────────
    const initializeFormData = (upgrade = null) => {
        if (upgrade) {
            // Bestehende Daten laden — backward-compat: falls noch kein variants_by_rarity
            const vbr = upgrade.variants_by_rarity?.length
                ? upgrade.variants_by_rarity
                : [];
            setFormData({
                name: upgrade.name || '',
                description: upgrade.description || '',
                image_url: upgrade.image_url || '',
                variants_by_rarity: vbr,
            });
        } else {
            setFormData({ name: '', description: '', image_url: '', variants_by_rarity: [] });
        }
    };

    // ─── CRUD ───────────────────────────────────────────────────────────────────
    const handleCreate = async () => {
        if (!formData.name.trim()) return;
        try {
            await upgradesApi.create({
                name: formData.name.trim(),
                description: formData.description || '',
                island_id: island.id,
                image_url: formData.image_url || null,
                variants_by_rarity: formData.variants_by_rarity,
            }, user?.discord_id, user?.username);
            setModalOpen(false);
            loadUpgrades();
            toast.success('Upgrade erstellt');
        } catch {
            toast.error('Fehler beim Erstellen des Upgrades');
        }
    };

    const handleUpdate = async () => {
        if (!formData.name.trim() || !editingUpgrade) return;
        try {
            await upgradesApi.update(editingUpgrade.id, {
                name: formData.name.trim(),
                description: formData.description || '',
                island_id: island.id,
                image_url: formData.image_url || null,
                variants_by_rarity: formData.variants_by_rarity,
            }, user?.discord_id, user?.username);
            setEditingUpgrade(null);
            setModalOpen(false);
            toast.success('Upgrade aktualisiert');
            loadUpgrades();
        } catch {
            toast.error('Fehler beim Aktualisieren des Upgrades');
        }
    };

    const handleDelete = async () => {
        if (!upgradeToDelete) return;
        try {
            await upgradesApi.delete(upgradeToDelete.id, user?.discord_id, user?.username);
            toast.success('Upgrade gelöscht');
            setDeleteModalOpen(false);
            setUpgradeToDelete(null);
            loadUpgrades();
        } catch {
            toast.error('Fehler beim Löschen des Upgrades');
        }
    };

    // ─── Seltenheits-Helfer ─────────────────────────────────────────────────────
    const getRarityIndex = (rarity) =>
        formData.variants_by_rarity.findIndex(r => r.rarity === rarity);

    const ensureRarity = (rarity) => {
        const idx = getRarityIndex(rarity);
        if (idx === -1) {
            setFormData(prev => ({
                ...prev,
                variants_by_rarity: [...prev.variants_by_rarity, { rarity, variants: [] }],
            }));
        }
        return idx;
    };

    // ─── Varianten-Helfer ───────────────────────────────────────────────────────
    const addVariant = (rarity) => {
        const idx = getRarityIndex(rarity);
        if (idx === -1) {
            // Seltenheit noch nicht vorhanden → neu anlegen
            setFormData(prev => ({
                ...prev,
                variants_by_rarity: [
                    ...prev.variants_by_rarity,
                    { rarity, variants: [makeVariant()] },
                ],
            }));
            return;
        }
        setFormData(prev => {
            const updated = [...prev.variants_by_rarity];
            updated[idx] = {
                ...updated[idx],
                variants: [...updated[idx].variants, makeVariant()],
            };
            return { ...prev, variants_by_rarity: updated };
        });
    };

    const removeVariant = (rarityIdx, variantIdx) => {
        setFormData(prev => {
            const updated = [...prev.variants_by_rarity];
            updated[rarityIdx] = {
                ...updated[rarityIdx],
                variants: updated[rarityIdx].variants.filter((_, i) => i !== variantIdx),
            };
            return { ...prev, variants_by_rarity: updated };
        });
    };

    const updateVariantName = (rarityIdx, variantIdx, name) => {
        setFormData(prev => {
            const updated = [...prev.variants_by_rarity];
            const variants = [...updated[rarityIdx].variants];
            variants[variantIdx] = { ...variants[variantIdx], variant_name: name };
            updated[rarityIdx] = { ...updated[rarityIdx], variants };
            return { ...prev, variants_by_rarity: updated };
        });
    };

    // ─── Stufen-Helfer ──────────────────────────────────────────────────────────
    const addLevel = (rarityIdx, variantIdx) => {
        setFormData(prev => {
            const updated = [...prev.variants_by_rarity];
            const variants = [...updated[rarityIdx].variants];
            const currentLevels = variants[variantIdx].levels || [];
            const nextLevel = currentLevels.length + 1;
            variants[variantIdx] = {
                ...variants[variantIdx],
                levels: [...currentLevels, makeLevel(nextLevel)],
            };
            updated[rarityIdx] = { ...updated[rarityIdx], variants };
            return { ...prev, variants_by_rarity: updated };
        });
    };

    const removeLevel = (rarityIdx, variantIdx, levelIdx) => {
        setFormData(prev => {
            const updated = [...prev.variants_by_rarity];
            const variants = [...updated[rarityIdx].variants];
            const newLevels = variants[variantIdx].levels
                .filter((_, i) => i !== levelIdx)
                .map((l, i) => ({ ...l, level: i + 1 }));
            variants[variantIdx] = { ...variants[variantIdx], levels: newLevels };
            updated[rarityIdx] = { ...updated[rarityIdx], variants };
            return { ...prev, variants_by_rarity: updated };
        });
    };

    // ─── Feld-Helfer ────────────────────────────────────────────────────────────
    const addField = (rarityIdx, variantIdx, levelIdx) => {
        setFormData(prev => {
            const updated = [...prev.variants_by_rarity];
            const variants = [...updated[rarityIdx].variants];
            const levels = [...variants[variantIdx].levels];
            levels[levelIdx] = {
                ...levels[levelIdx],
                fields: [...(levels[levelIdx].fields || []), { name: '', value: '' }],
            };
            variants[variantIdx] = { ...variants[variantIdx], levels };
            updated[rarityIdx] = { ...updated[rarityIdx], variants };
            return { ...prev, variants_by_rarity: updated };
        });
    };

    const removeField = (rarityIdx, variantIdx, levelIdx, fieldIdx) => {
        setFormData(prev => {
            const updated = [...prev.variants_by_rarity];
            const variants = [...updated[rarityIdx].variants];
            const levels = [...variants[variantIdx].levels];
            levels[levelIdx] = {
                ...levels[levelIdx],
                fields: levels[levelIdx].fields.filter((_, i) => i !== fieldIdx),
            };
            variants[variantIdx] = { ...variants[variantIdx], levels };
            updated[rarityIdx] = { ...updated[rarityIdx], variants };
            return { ...prev, variants_by_rarity: updated };
        });
    };

    const updateField = (rarityIdx, variantIdx, levelIdx, fieldIdx, key, value) => {
        setFormData(prev => {
            const updated = [...prev.variants_by_rarity];
            const variants = [...updated[rarityIdx].variants];
            const levels = [...variants[variantIdx].levels];
            const fields = levels[levelIdx].fields.map((f, i) =>
                i === fieldIdx ? { ...f, [key]: value } : f
            );
            levels[levelIdx] = { ...levels[levelIdx], fields };
            variants[variantIdx] = { ...variants[variantIdx], levels };
            updated[rarityIdx] = { ...updated[rarityIdx], variants };
            return { ...prev, variants_by_rarity: updated };
        });
    };

    // ─── Hilfsfunktion: Zähle Varianten pro Seltenheit ─────────────────────────
    const getVariantCount = (upgrade, rarity) => {
        const rbr = upgrade.variants_by_rarity || [];
        const entry = rbr.find(r => r.rarity === rarity);
        return entry?.variants?.length || 0;
    };

    // ─── Guard ──────────────────────────────────────────────────────────────────
    if (!island) {
        return (
            <div className="p-8 text-center text-gray-500 border border-dashed border-white/10 rounded-xl">
                <ArrowUpCircle size={48} className="mx-auto mb-4 text-gray-600" />
                <p>Wähle eine Insel aus, um Upgrades zu verwalten</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-['Rajdhani'] font-semibold text-lg text-white">
                        Upgrades auf {island.name}
                    </h3>
                    <p className="text-xs text-gray-500">
                        Verwalte Upgrades mit Seltenheiten, Varianten und Stufen
                    </p>
                </div>
                <Button
                    onClick={() => { setEditingUpgrade(null); initializeFormData(); setModalOpen(true); }}
                    size="sm"
                    data-testid="create-upgrade-btn"
                    className="bg-green-500 hover:bg-green-400 text-black"
                >
                    <Plus size={16} className="mr-1" />
                    Neues Upgrade
                </Button>
            </div>

            {/* Upgrades List */}
            <div className="space-y-3">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Laden...</div>
                ) : upgrades.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 border border-dashed border-white/10 rounded-xl">
                        Noch keine Upgrades auf dieser Insel
                    </div>
                ) : (
                    upgrades.map((upgrade, index) => (
                        <div
                            key={upgrade.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            data-testid={`upgrade-item-${upgrade.id}`}
                            className={cn(
                                'rounded-xl border border-white/10 bg-white/5 overflow-hidden transition-opacity',
                                draggedItem === index && 'opacity-50'
                            )}
                        >
                            {/* Row */}
                            <div
                                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5 transition-colors"
                                onClick={() => setExpandedUpgrade(
                                    expandedUpgrade === upgrade.id ? null : upgrade.id
                                )}
                            >
                                <div className="cursor-grab active:cursor-grabbing text-gray-500 hover:text-white">
                                    <GripVertical size={18} />
                                </div>
                                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-green-500/10">
                                    <img
                                        src={upgrade.image_url || PLACEHOLDER_IMAGE}
                                        alt={upgrade.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-['Rajdhani'] font-semibold text-white truncate">
                                        {upgrade.name}
                                    </h4>
                                    {upgrade.description && (
                                        <p className="text-sm text-gray-500 truncate">{upgrade.description}</p>
                                    )}
                                    {/* Seltenheits-Badges mit Varianten-Anzahl */}
                                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                                        {(upgrade.variants_by_rarity || []).filter(r => r.variants?.length > 0).map(r => {
                                            const c = RARITY_COLORS[r.rarity] || RARITY_COLORS.Common;
                                            return (
                                                <span key={r.rarity} className={cn(
                                                    'px-1.5 py-0.5 rounded text-xs border flex items-center gap-1',
                                                    c.badge
                                                )}>
                                                    {r.rarity}
                                                    <span className="opacity-60">×{r.variants.length}</span>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingUpgrade(upgrade);
                                            initializeFormData(upgrade);
                                            setModalOpen(true);
                                        }}
                                        className="text-gray-400 hover:text-green-400 hover:bg-green-500/10"
                                    >
                                        <Pencil size={16} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setUpgradeToDelete(upgrade);
                                            setDeleteModalOpen(true);
                                        }}
                                        className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                    {expandedUpgrade === upgrade.id
                                        ? <ChevronUp size={20} className="text-gray-500" />
                                        : <ChevronDown size={20} className="text-gray-500" />
                                    }
                                </div>
                            </div>

                            {/* Expanded Preview */}
                            {expandedUpgrade === upgrade.id && (
                                <div className="border-t border-white/10 p-4 bg-black/20 space-y-4">
                                    {(upgrade.variants_by_rarity || []).map(rarityEntry => {
                                        const c = RARITY_COLORS[rarityEntry.rarity] || RARITY_COLORS.Common;
                                        return (
                                            <div key={rarityEntry.rarity} className={cn(
                                                'rounded-lg border p-3',
                                                c.border, c.bg
                                            )}>
                                                <span className={cn('font-semibold text-sm', c.text)}>
                                                    {rarityEntry.rarity}
                                                </span>
                                                <div className="mt-2 space-y-2">
                                                    {(rarityEntry.variants || []).map((variant, vi) => {
                                                        const isMaxed = variant.levels?.length > 0;
                                                        const lastLevel = variant.levels?.[variant.levels.length - 1];
                                                        return (
                                                            <div key={vi} className="flex items-center justify-between text-sm py-1 border-b border-white/5 last:border-0">
                                                                <div className="flex items-center gap-2">
                                                                    <Layers size={12} className="text-gray-500" />
                                                                    <span className="text-white">{variant.variant_name || `Variante ${vi + 1}`}</span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-gray-500 text-xs font-mono">
                                                                        {variant.levels?.length || 0} Stufen
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    {(!rarityEntry.variants || rarityEntry.variants.length === 0) && (
                                                        <p className="text-xs text-gray-600 italic">Keine Varianten</p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {(!upgrade.variants_by_rarity || upgrade.variants_by_rarity.length === 0) && (
                                        <p className="text-sm text-gray-500 italic">Keine Seltenheiten konfiguriert</p>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* ═══════════════════════════════════════════════════════════════════
                CREATE / EDIT MODAL
            ════════════════════════════════════════════════════════════════════ */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-3xl max-h-[92vh] bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="font-['Rajdhani'] text-xl text-white">
                            {editingUpgrade ? 'Upgrade bearbeiten' : 'Neues Upgrade erstellen'}
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="max-h-[calc(92vh-180px)] pr-4">
                        <div className="space-y-6 pb-2">

                            {/* Basis-Infos */}
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label className="text-gray-400">Name *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                                        placeholder="Upgrade Name (z.B. Angriffskraft)"
                                        data-testid="upgrade-name-input"
                                        className="bg-black/40 border-white/10"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-gray-400">Beschreibung</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                                        placeholder="Optionale Beschreibung..."
                                        rows={2}
                                        className="bg-black/40 border-white/10 resize-none"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-gray-400">Bild</Label>
                                    <ImageUpload
                                        currentImage={formData.image_url}
                                        onUpload={url => setFormData(p => ({ ...p, image_url: url || '' }))}
                                    />
                                    <div className="text-xs text-gray-500">Oder URL direkt eingeben:</div>
                                    <Input
                                        value={formData.image_url}
                                        onChange={e => setFormData(p => ({ ...p, image_url: e.target.value }))}
                                        placeholder="https://..."
                                        className="bg-black/40 border-white/10"
                                    />
                                </div>
                            </div>

                            {/* ─── Seltenheiten mit Varianten & Stufen ──────────────────── */}
                            <div className="space-y-2">
                                <Label className="text-gray-400 text-base">Seltenheiten & Varianten</Label>
                                <p className="text-xs text-gray-600">
                                    Füge pro Seltenheit beliebig viele Varianten hinzu. Jede Variante hat eigene Upgrade-Stufen mit frei benennbaren Feldern.
                                </p>
                            </div>

                            {RARITIES.map(rarity => {
                                const rarityIdx = formData.variants_by_rarity.findIndex(r => r.rarity === rarity);
                                const rarityData = rarityIdx !== -1
                                    ? formData.variants_by_rarity[rarityIdx]
                                    : { rarity, variants: [] };
                                const c = RARITY_COLORS[rarity] || RARITY_COLORS.Common;

                                return (
                                    <div
                                        key={rarity}
                                        className={cn('rounded-xl border p-4 space-y-3', c.border, c.bg)}
                                    >
                                        {/* Seltenheits-Header */}
                                        <div className="flex items-center justify-between">
                                            <span className={cn('font-bold font-[\'Rajdhani\'] text-base', c.text)}>
                                                {rarity}
                                            </span>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => addVariant(rarity)}
                                                className={cn(
                                                    'border-white/20 text-xs',
                                                    `hover:${c.text} hover:border-current`
                                                )}
                                            >
                                                <Plus size={13} className="mr-1" />
                                                Variante hinzufügen
                                            </Button>
                                        </div>

                                        {/* Varianten */}
                                        {rarityData.variants.length === 0 && (
                                            <p className="text-xs text-gray-600 italic py-1">
                                                Keine Varianten — klicke "Variante hinzufügen"
                                            </p>
                                        )}

                                        {rarityData.variants.map((variant, variantIdx) => (
                                            <div
                                                key={variantIdx}
                                                className="rounded-lg bg-black/30 border border-white/10 p-3 space-y-3"
                                            >
                                                {/* Varianten-Name */}
                                                <div className="flex items-center gap-2">
                                                    <Layers size={14} className="text-gray-500 shrink-0" />
                                                    <Input
                                                        value={variant.variant_name}
                                                        onChange={e => updateVariantName(rarityIdx, variantIdx, e.target.value)}
                                                        placeholder={`Varianten-Name (z.B. "+5 Angriff")`}
                                                        className="flex-1 bg-black/40 border-white/10 h-8 text-sm"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => addLevel(rarityIdx, variantIdx)}
                                                        className="text-xs text-cyan-400 hover:text-cyan-300 shrink-0"
                                                    >
                                                        <Plus size={12} className="mr-1" />
                                                        Stufe
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeVariant(rarityIdx, variantIdx)}
                                                        className="h-8 w-8 text-gray-500 hover:text-red-400 shrink-0"
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>

                                                {/* Stufen */}
                                                {(variant.levels || []).map((levelData, levelIdx) => {
                                                    const isLastLevel = levelIdx === (variant.levels.length - 1);
                                                    return (
                                                        <div
                                                            key={levelIdx}
                                                            className={cn(
                                                                'rounded-lg border p-3 space-y-2',
                                                                isLastLevel
                                                                    ? 'border-yellow-500/30 bg-yellow-500/5'
                                                                    : 'border-green-500/20 bg-green-500/5'
                                                            )}
                                                        >
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2">
                                                                    <span className={cn(
                                                                        'text-sm font-semibold',
                                                                        isLastLevel ? 'text-yellow-400' : 'text-green-400'
                                                                    )}>
                                                                        Stufe {levelData.level}
                                                                    </span>
                                                                    {isLastLevel && (
                                                                        <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                                                                            <Crown size={10} />
                                                                            Max
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-1">
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => addField(rarityIdx, variantIdx, levelIdx)}
                                                                        className="text-xs text-cyan-400 h-7 px-2"
                                                                    >
                                                                        <Plus size={11} className="mr-1" />
                                                                        Feld
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={() => removeLevel(rarityIdx, variantIdx, levelIdx)}
                                                                        className="h-7 w-7 text-gray-500 hover:text-red-400"
                                                                    >
                                                                        <Trash2 size={12} />
                                                                    </Button>
                                                                </div>
                                                            </div>

                                                            {/* Felder */}
                                                            {(levelData.fields || []).map((field, fieldIdx) => (
                                                                <div key={fieldIdx} className="flex gap-2">
                                                                    <Input
                                                                        value={field.name}
                                                                        onChange={e => updateField(rarityIdx, variantIdx, levelIdx, fieldIdx, 'name', e.target.value)}
                                                                        placeholder="Attribut"
                                                                        className="flex-1 bg-black/40 border-white/10 h-8 text-xs"
                                                                    />
                                                                    <Input
                                                                        value={field.value}
                                                                        onChange={e => updateField(rarityIdx, variantIdx, levelIdx, fieldIdx, 'value', e.target.value)}
                                                                        placeholder="Wert"
                                                                        className="flex-1 bg-black/40 border-white/10 h-8 text-xs"
                                                                    />
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={() => removeField(rarityIdx, variantIdx, levelIdx, fieldIdx)}
                                                                        className="h-8 w-8 text-gray-500 hover:text-red-400"
                                                                    >
                                                                        <Trash2 size={12} />
                                                                    </Button>
                                                                </div>
                                                            ))}

                                                            {levelData.fields.length === 0 && (
                                                                <p className="text-xs text-gray-600 italic">
                                                                    Klicke "+ Feld" um Werte hinzuzufügen
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {variant.levels.length === 0 && (
                                                    <p className="text-xs text-gray-600 italic pl-1">
                                                        Klicke "+ Stufe" um Stufen hinzuzufügen
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollArea>

                    <DialogFooter className="gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => setModalOpen(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={editingUpgrade ? handleUpdate : handleCreate}
                            disabled={!formData.name.trim()}
                            data-testid="save-upgrade-btn"
                            className="bg-green-500 hover:bg-green-400 text-black"
                        >
                            {editingUpgrade ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
                <DialogContent className="max-w-sm bg-[#0A0A0A] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="font-['Rajdhani'] text-xl text-red-400">
                            Upgrade löschen?
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-gray-400">
                        Möchtest du{' '}
                        <span className="text-white font-semibold">"{upgradeToDelete?.name}"</span>{' '}
                        wirklich löschen?
                    </p>
                    <DialogFooter className="gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                            className="border-white/10 text-gray-400"
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-400 text-white"
                        >
                            Löschen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
